import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Target, MessageCircle, Calendar, Lightbulb, UserPlus } from 'lucide-react';

const AdhesionSectionIntegrated: React.FC = () => {
    const benefits = [
        "Participer activement aux décisions qui façonnent l'avenir du Gabon",
        "Accéder à un réseau national de leaders et d'entrepreneurs engagés",
        "Bénéficier de formations exclusives en leadership politique",
        "Contribuer directement aux projets de développement local"
    ];

    return (
        <>
            <section id="adhesion" className="relative min-h-screen">
                <div className="flex flex-col lg:flex-row min-h-screen">

                    {/* Côté gauche - Fond sombre avec contenu */}
                    <div className="lg:w-1/2 bg-gray-900 text-white p-8 lg:p-16 flex items-center">
                        <div className="w-full max-w-xl mx-auto">
                            <div className="mb-12">
                                <div className="w-20 h-1 bg-primary-yellow mb-8"></div>
                                <h1 className="text-4xl lg:text-5xl font-light mb-6 leading-tight">
                                    Rejoignez un mouvement
                                    <span className="block text-primary-yellow">qui transforme le Gabon</span>
                                </h1>
                                <p className="text-xl text-gray-300 font-light">
                                    Plus de 10 000 citoyens engagés construisent déjà l'avenir de notre nation.
                                </p>
                            </div>

                            {/* Avantages */}
                            <div className="space-y-4 mb-12">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-start space-x-3">
                                        <Check className="w-5 h-5 text-primary-green mt-0.5 flex-shrink-0"/>
                                        <p className="text-gray-300 font-light">{benefit}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Statistiques */}
                            <div className="grid grid-cols-3 gap-8">
                                <div>
                                    <div className="text-3xl font-light text-primary-yellow">9</div>
                                    <div className="text-sm text-gray-400">Provinces</div>
                                </div>
                                <div>
                                    <div className="text-3xl font-light text-primary-green">50+</div>
                                    <div className="text-sm text-gray-400">Projets</div>
                                </div>
                                <div>
                                    <div className="text-3xl font-light text-white">24/7</div>
                                    <div className="text-sm text-gray-400">Support</div>
                                </div>
                            </div>

                            {/* Citation */}
                            <blockquote className="mt-12 pt-12 border-t border-gray-800">
                                <p className="text-lg font-light italic text-gray-300">
                                    "Ensemble, nous avons le pouvoir de bâtir le Gabon que nos enfants méritent."
                                </p>
                                <cite className="text-sm text-gray-400 mt-2 block">
                                    — Brice Clotaire Oligui Nguema
                                </cite>
                            </blockquote>
                        </div>
                    </div>

                    {/* Côté droit - Call to Action */}
                    <div className="lg:w-1/2 bg-white p-8 lg:p-16 flex items-center">
                        <div className="w-full max-w-xl mx-auto text-center">
                            <div className="mb-8">
                                <div
                                    className="w-20 h-20 bg-gradient-to-br from-primary-blue to-primary-green mx-auto mb-6 flex items-center justify-center">
                                    <UserPlus className="w-10 h-10 text-white"/>
                                </div>

                                <h2 className="text-3xl font-light text-gray-900 mb-4">
                                    Devenez membre
                                </h2>
                                <p className="text-gray-600 mb-8">
                                    Rejoignez l'Union Démocratique des Bâtisseurs et participez activement à la construction d'un
                                    Gabon moderne et prospère.
                                </p>
                            </div>

                            {/* Avantages express */}
                            <div className="grid grid-cols-2 gap-6 mb-8 text-left">
                                <div className="bg-gray-50 p-4">
                                    <Target className="w-6 h-6 text-primary-blue mb-2"/>
                                    <h4 className="font-medium text-gray-900 mb-1">Impact direct</h4>
                                    <p className="text-sm text-gray-600">Participez aux décisions importantes</p>
                                </div>
                                <div className="bg-gray-50 p-4">
                                    <MessageCircle className="w-6 h-6 text-primary-green mb-2"/>
                                    <h4 className="font-medium text-gray-900 mb-1">Réseau national</h4>
                                    <p className="text-sm text-gray-600">Connectez-vous avec des leaders</p>
                                </div>
                                <div className="bg-gray-50 p-4">
                                    <Calendar className="w-6 h-6 text-primary-yellow mb-2"/>
                                    <h4 className="font-medium text-gray-900 mb-1">Formations</h4>
                                    <p className="text-sm text-gray-600">Développez vos compétences</p>
                                </div>
                                <div className="bg-gray-50 p-4">
                                    <Lightbulb className="w-6 h-6 text-primary-blue mb-2"/>
                                    <h4 className="font-medium text-gray-900 mb-1">Innovation</h4>
                                    <p className="text-sm text-gray-600">Proposez vos idées</p>
                                </div>
                            </div>

                            {/* Bouton principal - Lien vers la page d'adhésion */}
                            <Link
                                to="/adhesion"
                                className="w-full bg-primary-blue text-white py-4 text-lg font-medium hover:bg-primary-green transition-colors flex items-center justify-center space-x-2 group mb-6"
                            >
                                <span>Commencer mon adhésion</span>
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform"/>
                            </Link>

                            {/* Info légale */}
                            <p className="text-sm text-gray-500">
                                Processus 100% sécurisé • Adhésion gratuite • Confirmation immédiate
                            </p>
                        </div>
                    </div>
                </div>

                {/* Section bénévolat */}
                <div className="relative bg-gray-900 py-24 overflow-hidden">
                    <div className="relative z-10 container mx-auto px-4">
                        <div className="max-w-6xl mx-auto">
                            <div className="text-center mb-16">
                                <h3 className="text-4xl lg:text-5xl font-light text-white mb-4">
                                    Votre engagement
                                    <span className="block text-primary-yellow">fait la différence</span>
                                </h3>
                                <p className="text-xl text-gray-400 font-light max-w-3xl mx-auto">
                                    Rejoignez des milliers de citoyens qui transforment déjà le Gabon par leur action
                                    quotidienne
                                </p>
                            </div>

                            {/* Statistiques */}
                            <div className="grid md:grid-cols-3 gap-8 mb-16">
                                <div className="group cursor-pointer">
                                    <div
                                        className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 p-8 hover:border-primary-yellow transition-all hover:transform hover:scale-105">
                                        <div
                                            className="text-6xl font-light text-primary-yellow mb-4 group-hover:scale-110 transition-transform">
                                            5000+
                                        </div>
                                        <h4 className="text-xl text-white mb-2">Bénévoles actifs</h4>
                                        <p className="text-gray-400 text-sm">
                                            Dans toutes les provinces, nos bénévoles sont le cœur battant du mouvement
                                        </p>
                                    </div>
                                </div>

                                <div className="group cursor-pointer">
                                    <div
                                        className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 p-8 hover:border-primary-green transition-all hover:transform hover:scale-105">
                                        <div
                                            className="text-6xl font-light text-primary-green mb-4 group-hover:scale-110 transition-transform">
                                            20+
                                        </div>
                                        <h4 className="text-xl text-white mb-2">Événements mensuels</h4>
                                        <p className="text-gray-400 text-sm">
                                            Rencontres, formations et actions sur le terrain chaque semaine
                                        </p>
                                    </div>
                                </div>

                                <div className="group cursor-pointer">
                                    <div
                                        className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 p-8 hover:border-primary-blue transition-all hover:transform hover:scale-105">
                                        <div
                                            className="text-6xl font-light text-primary-blue mb-4 group-hover:scale-110 transition-transform">
                                            100%
                                        </div>
                                        <h4 className="text-xl text-white mb-2">Impact positif</h4>
                                        <p className="text-gray-400 text-sm">
                                            Chaque action compte pour construire le Gabon de demain
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* CTA final */}
                            <div className="text-center">
                                <Link
                                    to="/adhesion"
                                    className="bg-white text-gray-900 px-8 py-3 font-normal hover:bg-gray-100 transition-colors inline-flex items-center space-x-2 group"
                                >
                                    <span>Commencez votre engagement</span>
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default AdhesionSectionIntegrated;
